import { useEffect, useState } from "react";
import { RIF, clearIdentitySession, useTranslation } from "../../lib";
import {
  useCreateDispatchActions,
  useSelectAuth,
  useSelectConnectivityState,
  useSelectTheme,
} from "../../store";
import { Button, ButtonText } from "..";

/** How long the ordinary-503 notice stays up before it dismisses itself. */
export const UNAVAILABLE_NOTICE_MS = 8000;

/**
 * The one place the app admits it cannot reach the backend, in four variants.
 *
 * Checking: the idle tracker has marked the session unverified (`auth.sessionUnverified`) and no
 * attempt to reach the server has failed yet. The private pages are hidden meanwhile, so the bar
 * says why and offers nothing; once an attempt fails the reconnecting variant takes over.
 *
 * Reconnecting: `auth.refreshUnavailable` is set, so the refresh coordinator is retrying on a
 * schedule. The bar counts down to `auth.refreshNextRetryAt`, offers "Retry now" (the coordinator
 * races on `AUTH_REFRESH_RETRY_NOW`) and always offers the way out -- a retry loop the user cannot
 * leave would trap a first-time visitor and anyone sitting out a long migration halt.
 *
 * Unavailable: an ordinary request answered 502/503/504 or did not answer. There is nothing
 * to retry automatically, so the bar says so for a few seconds and goes away. It is suppressed
 * while the reconnecting variant is up: the reconnecting one is the fuller story.
 *
 * Out of date: a request failed during a reconnecting outage (`connectivity.failedDuringOutage`)
 * and the session has since recovered. Nothing re-runs what failed, so the bar says the page may
 * be out of date and offers a reload. It has no timer -- the app looking healthy over out-of-date
 * data is what it is there to prevent -- and it outranks the unavailable variant, whose story it
 * already tells.
 *
 * It sits at z-index 120, above `Loading` (110) and the login layer (102), so
 * "Retry now" stays clickable while a session popup is open. No variant is permanent -- one ends
 * with the next successful refresh, one when the session is confirmed or ends, one after
 * `UNAVAILABLE_NOTICE_MS` and one when the user reloads or dismisses it -- so the navbar underneath
 * is only ever covered while something is actually wrong.
 */
export const ConnectionNoticeBanner = () => {
  const { t } = useTranslation();
  const { color, pad, fontSize, fontWeight, borderRadius } = useSelectTheme();
  const { doAUTH_REFRESH_RETRY_NOW, doCONNECTIVITY_FAILED_DURING_OUTAGE_SET } =
    useCreateDispatchActions();
  const auth = useSelectAuth();
  const { serviceUnavailableAt, failedDuringOutage } = useSelectConnectivityState();

  const reconnecting = Boolean(auth?.refreshUnavailable);
  const checking = Boolean(auth?.sessionUnverified) && !reconnecting;
  const isRefreshing = Boolean(auth?.isRefreshing);
  const nextRetryAt: number | null = auth?.refreshNextRetryAt ?? null;

  const [nowMs, setNowMs] = useState(() => Date.now());
  const [unavailableVisible, setUnavailableVisible] = useState(false);

  // One tick per second while a retry is scheduled, and no ticking at all otherwise. The interval
  // is keyed on `reconnecting` rather than on `nextRetryAt`, so `AUTH_REFRESHING` -- which leaves
  // the transient fields alone -- does not restart it mid-cycle.
  useEffect(() => {
    if (!reconnecting) return;
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [reconnecting]);

  // The notice is dated, not flagged, so a stamp that is already older than the window never opens
  // it at all and a fresh 503 restarts the window by changing the timestamp.
  useEffect(() => {
    if (!serviceUnavailableAt) {
      setUnavailableVisible(false);
      return;
    }

    const remainingMs = serviceUnavailableAt + UNAVAILABLE_NOTICE_MS - Date.now();

    if (remainingMs <= 0) {
      setUnavailableVisible(false);
      return;
    }

    setUnavailableVisible(true);
    const timer = window.setTimeout(() => setUnavailableVisible(false), remainingMs);
    return () => window.clearTimeout(timer);
  }, [serviceUnavailableAt]);

  // Clamped at zero while an attempt is in flight and whenever no moment is scheduled yet, so the
  // bar never counts into negative seconds or prints NaN.
  const secondsRemaining =
    isRefreshing || !nextRetryAt ? 0 : Math.max(0, Math.ceil((nextRetryAt - nowMs) / 1000));

  // the checking variant outranks the two below for the same reason the reconnecting one does: the
  // session itself is in question, and the bar holds one story at a time
  const showOutOfDate = Boolean(failedDuringOutage) && !reconnecting && !checking;
  const showUnavailable = unavailableVisible && !reconnecting && !checking && !showOutOfDate;

  // in the same order of precedence as the variants above
  const message = () => {
    if (reconnecting)
      return isRefreshing ? t("connection.retrying_message") : t("connection.reconnecting_message");
    if (checking) return t("connection.checking_session_message");
    if (showOutOfDate) return t("connection.out_of_date_message");
    return t("connection.unavailable_message");
  };

  const handleRetryNow = () => doAUTH_REFRESH_RETRY_NOW();

  const handleReload = () => window.location.reload();

  // A 503 stamped during the outage leaves the unavailable notice armed underneath this one. The bar
  // has just said the connection is back, so dismissing it must not swap in "temporarily
  // unavailable"; a later 503 restamps `serviceUnavailableAt` and opens the notice again.
  const handleDismissOutOfDate = () => {
    setUnavailableVisible(false);
    doCONNECTIVITY_FAILED_DURING_OUTAGE_SET({ failedDuringOutage: false });
  };

  // Same exit as the navbar's Logout: end the session on the backend, then hard-navigate so every
  // in-memory slice goes with it.
  const handleSignInAgain = async () => {
    await clearIdentitySession();
    window.location.href = "/";
  };

  return (
    <>
      {RIF(
        reconnecting || checking || showOutOfDate || showUnavailable,
        <div
          data-testid="connection_notice_banner"
          role="status"
          aria-live="polite"
          css={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            boxSizing: "border-box",
            zIndex: 120,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            columnGap: pad.mediumSmall,
            rowGap: pad.xs,
            padding: `${pad.small} ${pad.medium}`,
            background: color.surface.attention,
            borderBottom: `1px solid ${color.border.warning}`,
            color: color.textIcon.title,
            fontSize: fontSize.h6,
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.13)",
          }}
        >
          <span
            data-testid="connection_notice_message"
            css={{ fontWeight: fontWeight.medium, textAlign: "center", minWidth: 0 }}
          >
            {message()}
          </span>

          {RIF(
            reconnecting,
            <>
              {/* Hidden from assistive tech on purpose: the bar is a polite live region, and a
                  number that changes every second would re-announce the whole notice each tick. */}
              <span
                data-testid="connection_notice_countdown"
                aria-hidden="true"
                css={{
                  flex: "none",
                  minWidth: "34px",
                  padding: `0 ${pad.xs}`,
                  borderRadius: borderRadius.default,
                  border: `1px solid ${color.border._160}`,
                  background: color.white,
                  color: color.textIcon.secondary,
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: fontWeight.bold,
                  textAlign: "center",
                }}
              >
                {secondsRemaining}s
              </span>
              <Button
                data-testid="connection_notice_retry_btn"
                type="button"
                btnPadding="mediumSmall"
                disabled={isRefreshing}
                onClick={handleRetryNow}
              >
                {t("connection.retry_now_button")}
              </Button>
              <ButtonText onClick={handleSignInAgain}>
                {t("connection.sign_in_again_button")}
              </ButtonText>
            </>,
          )}

          {RIF(
            showOutOfDate,
            <>
              <Button
                data-testid="connection_notice_reload_btn"
                type="button"
                btnPadding="mediumSmall"
                onClick={handleReload}
              >
                {t("connection.reload_button")}
              </Button>
              <ButtonText onClick={handleDismissOutOfDate}>
                {t("connection.dismiss_button")}
              </ButtonText>
            </>,
          )}
        </div>,
      )}
    </>
  );
};
